import { Box, Button, Typography, useTheme } from "@outschool/backpack";
import { EVENT_SOURCE } from "@outschool/experiments-shared";
import { AutoAppliedFilters } from "@outschool/gql-backend-generated";
import {
  SearchUseCase,
  SpellingSuggestionType
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import {
  BannerType,
  ListingsPromoBanner
} from "@outschool/ui-components-contentful";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useSearchQuery } from "@outschool/ui-components-website";
import { useServerExperimentContext } from "@outschool/ui-experiments";
import {
  Container,
  Loading,
  TrackingPage
} from "@outschool/ui-legacy-component-library";
import { simpleRandomId, useOnScreen } from "@outschool/ui-utils";
import { omit } from "lodash";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";

import { SearchSuggestionsProvider } from "../../../../shared/client/providers/SearchSuggestionsProvider";
import { FEATURE_FLAGS } from "../../../../shared/Env";
import * as Env from "../../../../shared/Env";
import * as helpers from "../../../../shared/helpers";
import useClassTrackSearch from "../../../hooks/lib/useClassTrackSearch";
import { useTeacherSearchSuggestions } from "../../../hooks/lib/useTeacherSearchSuggestions";
import { useSpellingSuggestion } from "../../../hooks/SpellingSuggestion";
import { useSearchFilters } from "../../../lib/useSearchFilters";
import { useTimeZone } from "../../../providers/TimeZoneProvider";
// @ts-ignore
import ClassRequestForm from "../../ClassRequestForm";
import ErrorMessage from "../../ErrorMessage";
import SimpleLayout from "../../layouts/SimpleLayout";
import TeacherHeader from "../../teacher/TeacherHeader";
import TeacherSuggestions from "../../teacher/TeacherSuggestions";
import { uniqueIdFromFilters } from "../common";
import OtherListingsMask from "../OtherListingsMask";
import { addSearchToHistory } from "../RecentSearchHistory";
import ResultsCount from "../ResultsCount";
import ShowMore from "../ShowMore";
import TooSpecific from "../TooSpecific";
import { TopActivityList } from "../TopActivityList";
import useSelectedTeacher from "../useSelectedTeacher";
import ListingFilters from "./ListingFilters";
import { ListingsWrapper } from "./ListingsHelpers";

export default function SearchListings({
  onlineClassesPageImpressionId
}: {
  title?: string;
  description?: string;
  onlineClassesPageImpressionId?: string;
}) {
  const location = useLocation();
  const theme = useTheme();
  const itemsPerPage = 12;
  const { isLoggedIn, currentUserHasLoaded } = useSession();
  const { timeZone } = useTimeZone();
  const { loggedOutUserExperimentUid: anonymousId } =
    useServerExperimentContext();
  const [showOtherListings, setShowOtherListings] = useState(false);
  const { filters, setFilters, searchUid } = useSearchFilters();

  const { t } = useTranslation("client\\components\\search\\desktop\\Listings");

  const promotedRankingsEnabled: boolean =
    FEATURE_FLAGS.PROMOTED_SEARCH_LOGGING as boolean;

  const originalSpelling: boolean = filters?.originalSpelling === "yes";
  const { suggestion, loading: suggestionLoading } = useSpellingSuggestion({
    query: filters?.q || "",
    spellingSuggestionType: SpellingSuggestionType.SearchQuery
  });

  const forceSpellingCorrection =
    !originalSpelling &&
    !suggestionLoading &&
    suggestion?.highConfidence &&
    suggestion?.spellingSuggestion !== null;

  const emKeywords =
    forceSpellingCorrection && suggestion.spellingSuggestion.match(/\s/)
      ? suggestion?.spellingSuggestion
          .split(/\s/)
          .map((keyword: string) =>
            filters?.q?.includes(keyword) ? (
              <>{keyword} </>
            ) : (
              <Typography sx={{ fontWeight: "bold" }}>{keyword} </Typography>
            )
          )
      : suggestion?.spellingSuggestion;

  const showSpellingSuggestion =
    !originalSpelling &&
    !suggestionLoading &&
    !suggestion?.highConfidence &&
    suggestion?.spellingSuggestion &&
    suggestion?.spellingSuggestion != filters?.q;

  const enableFuzzySearch = !(
    forceSpellingCorrection ||
    showSpellingSuggestion ||
    originalSpelling
  );

  const queryOverride = forceSpellingCorrection
    ? { q: suggestion.spellingSuggestion }
    : {};

  const {
    loading,
    autoAppliedFilters,
    exactListings,
    otherListings,
    hasMore,
    exactTotal,
    exactHasMore,
    isFetchingMore,
    fetchMore,
    error,
    disabledAutoApplyFilters,
    setDisabledAutoApplyFilters
  } = useSearchQuery({
    referrer: document.referrer,
    skip: !currentUserHasLoaded,
    variables: {
      searchFilters: {
        ...omit({ ...filters, ...queryOverride }, [
          "userName",
          "originalSpelling"
        ]),
        timeZone
      },
      offset: 0,
      limit: itemsPerPage,
      skipRating: false,
      withRecentReviews: false,
      promotedRankingsEnabled,
      anonymousId,
      searchUid,
      pageImpressionId: onlineClassesPageImpressionId,
      isFuzzySearchEnabled: enableFuzzySearch,
      pageLocation: "DESKTOP_LISTING",
      useCase: SearchUseCase.Search
    }
  });

  useClassTrackSearch({
    currentUserHasLoaded,
    exactListings,
    itemsPerPage,
    loading
  });

  const availableListings = exactListings;
  // @ts-ignore TS(2345): Argument of type 'InputMaybe<string> | undefined' ... Remove this comment to see the full error message
  const teacherSuggestions = useTeacherSearchSuggestions(filters?.q, 10);

  const rootMargin = "-500px 0px 200px";
  // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
  const [endOfListRef, setEndOfListRef] = React.useState<HTMLDivElement>(null);
  const isEndOfListOnScreen: boolean = useOnScreen(
    endOfListRef,
    false,
    rootMargin
  );

  // We need to fire fetchMore() again if isAvailable changes and we haven't scrolled at all
  useEffect(() => {
    if (
      // end of list is not on screen
      !isEndOfListOnScreen ||
      // if reached end
      !hasMore ||
      //  or loading more results
      loading ||
      isFetchingMore ||
      //or hit network error
      error ||
      // or other listings is hidden
      (!exactHasMore && !showOtherListings)
    ) {
      return;
    }
    fetchMore();
  }, [
    fetchMore,
    isEndOfListOnScreen,
    hasMore,
    exactHasMore,
    error,
    loading,
    isFetchingMore,
    showOtherListings
  ]);

  // reset search state when filters change
  React.useEffect(() => {
    setShowOtherListings(false);
  }, [filters]);

  const hasTimeFilter = !!(
    filters?.time ||
    filters?.timeOfDay ||
    filters?.startAfter ||
    filters?.endBy ||
    filters?.startAfterTime ||
    filters?.endByTime ||
    filters?.dow
  );

  const pageImpressionId = React.useMemo(
    () => onlineClassesPageImpressionId ?? simpleRandomId(),
    [onlineClassesPageImpressionId]
  );

  const { loading: selectedTeacherLoading, selectedTeacher } =
    useSelectedTeacher(filters?.userUid);

  useEffect(() => {
    if (
      filters &&
      (((!loading || exactListings.length > 0) && filters.q) ||
        (filters && filters.userUid && filters.userName))
    ) {
      addSearchToHistory(filters);
    }
  }, [loading, exactListings, filters]);

  return (
    <TrackingPage
      name="activityListings"
      pageImpressionIdDefined={pageImpressionId}
      filters={filters}
      // @ts-ignore TS(2345): Argument of type 'ExtendedSearchFiltersType | unde... Remove this comment to see the full error message
      uniqueId={uniqueIdFromFilters("ActivityListingsPage", filters)}
    >
      {setImpressionNode => (
        <SimpleLayout
          sx={{ minHeight: "75%" }}
          hideReviewReminder
          // @ts-ignore TS(2322): Type 'Dispatch<HTMLElement>' is not assignable to ... Remove this comment to see the full error message
          ref={setImpressionNode}
          allowCategoriesNavbar={false}
          showSearchBox={false}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 200,
              backgroundColor: "primary.100"
            }}
          >
            <SearchSuggestionsProvider
              initialItem={{
                uid: helpers.uuidv4(),
                // eslint-disable-next-line i18next/no-literal-string
                type: "keyword",
                // @ts-ignore TS(2322): Type 'InputMaybe<string> | undefined' is not assig... Remove this comment to see the full error message
                name: filters.q
              }}
            >
              <ListingFilters
                autoAppliedFilters={autoAppliedFilters as AutoAppliedFilters}
                disabledAutoApplyFilters={disabledAutoApplyFilters}
                setDisabledAutoApplyFilters={setDisabledAutoApplyFilters}
                searchUid={searchUid}
              />
            </SearchSuggestionsProvider>
          </Box>
          {!error && !!selectedTeacher && (
            <Container fullBleed backgroundColor={theme.palette.primary[50]}>
              <TeacherHeader
                leader={selectedTeacher}
                includeProfileLink
                includeShareButton={false}
              />
            </Container>
          )}
          <Container fullBleed>
            <ListingsPromoBanner
              bannerType={BannerType.Search}
              eventSource={EVENT_SOURCE.BROWSER}
              queryParams={location.query}
            />
          </Container>
          <Container fullBleed sx={{ marginY: "medium" }}>
            {teacherSuggestions?.teachers.length &&
            !loading &&
            !suggestionLoading &&
            currentUserHasLoaded ? (
              <Box sx={{ paddingBottom: "2em" }}>
                <TeacherSuggestions
                  teacherSuggestions={teacherSuggestions}
                  searchUid={searchUid}
                />
              </Box>
            ) : null}
            <Box>
              <ResultsCount
                exactTotal={exactTotal}
                andMore={!!otherListings.length}
                loading={loading}
              />

              <TopActivityList />

              {error ? (
                <Box
                  sx={{
                    padding: "0.5em"
                  }}
                >
                  <ErrorMessage
                    value={error}
                    showStatusPageLink={Env.IS_READ_ONLY_MODE}
                  />
                </Box>
              ) : loading ||
                !currentUserHasLoaded ||
                suggestionLoading ||
                selectedTeacherLoading ? (
                <Loading delay={0} fullHeight />
              ) : (
                <Box>
                  {showSpellingSuggestion && (
                    <Box
                      sx={{
                        padding: "0.5em"
                      }}
                    >
                      {t`Did you mean: `}
                      <TrackedButton
                        trackingName="spelling_suggestion"
                        trackingTouchEventName="spelling_suggestion_clicked"
                        trackingUniqueId={searchUid}
                        trackingSharedProperties={{
                          ...suggestion,
                          anonymousId
                        }}
                        sx={{
                          marginTop: "-4px"
                        }}
                        color="primary"
                        size="medium"
                        variant="link"
                        onClick={() =>
                          setFilters?.("q", {
                            q: suggestion?.spellingSuggestion
                          })
                        }
                      >
                        {suggestion.spellingSuggestion}
                      </TrackedButton>
                    </Box>
                  )}
                  {forceSpellingCorrection && (
                    <Box
                      sx={{
                        padding: "0.5em"
                      }}
                    >
                      {t`Showing results for: `}
                      <Typography
                        sx={{
                          fontStyle: "italic"
                        }}
                      >
                        {emKeywords}
                      </Typography>
                      <br />
                      {t`Search instead for `}
                      <TrackedButton
                        trackingName="spelling_suggestion"
                        trackingTouchEventName="spelling_suggestion_original_clicked"
                        trackingUniqueId={searchUid}
                        trackingSharedProperties={{
                          ...suggestion,
                          anonymousId
                        }}
                        sx={{
                          marginTop: "-4px"
                        }}
                        color="primary"
                        size="medium"
                        variant="link"
                        onClick={() =>
                          setFilters?.("q", {
                            q: suggestion.query,
                            originalSpelling: "yes"
                          })
                        }
                      >
                        {suggestion.query}
                      </TrackedButton>
                    </Box>
                  )}
                  {availableListings?.length ? (
                    <ListingsWrapper
                      listings={availableListings}
                      tracking={{
                        startIndex: 0,
                        filters,
                        searchUid,
                        group: "exact_listings"
                      }}
                    />
                  ) : null}
                  {!exactHasMore && (
                    <Box
                      flex
                      sx={{
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center"
                      }}
                    >
                      {otherListings?.length > 0 ? (
                        <ShowMore
                          hasResults={availableListings?.length > 0}
                          hasTimeFilter={hasTimeFilter}
                          disabled={showOtherListings}
                          onClick={() => setShowOtherListings(true)}
                        />
                      ) : availableListings?.length === 0 ? (
                        <TooSpecific />
                      ) : null}
                    </Box>
                  )}
                  {otherListings && otherListings.length > 0 && (
                    <OtherListingsMask hidden={!showOtherListings}>
                      <ListingsWrapper
                        listings={otherListings}
                        tracking={{
                          startIndex: 0,
                          filters,
                          searchUid,
                          group: "other_listings"
                        }}
                      />
                    </OtherListingsMask>
                  )}
                  <br />
                  {isFetchingMore && (exactHasMore || showOtherListings) && (
                    <Loading delay={0} />
                  )}
                  <Box
                    sx={{
                      textAlign: "center",
                      position: "relative",
                      width: "100%",
                      maxWidth: "1024",
                      margin: "0 auto",
                      padding: "0 1em",
                      paddingTop: "3em",
                      paddingBottom: "3em"
                    }}
                    ref={setEndOfListRef}
                  >
                    {t`Looking for something different?`}
                    <Box
                      sx={{
                        paddingY: "1em",
                        marginBottom: "1em"
                      }}
                    >
                      <ClassRequestForm
                        isLoggedIn={isLoggedIn}
                        renderButton={(open: $TSFixMe) => (
                          <Button onClick={open}>{t`Request a Class`}</Button>
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Container>
        </SimpleLayout>
      )}
    </TrackingPage>
  );
}
